import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import confetti from 'canvas-confetti';
import { SmartFixSolution, Tracking } from '../../../../../../../../global/tracking/tracking';
import { ButtonComponent } from '../../../../../../../../shared/components/button/button.component';
import { LoadingSpinnerComponent } from '../../../../../../../../shared/components/forms/loading-spinner/loading-spinner.component';
import { RadioButtonComponent } from '../../../../../../../../shared/components/forms/radio-button/radio-button.component';
import { TextAreaComponent } from '../../../../../../../../shared/components/forms/text-area/text-area.component';
import { QMEncryptDirective } from '../../../../../../../../shared/directives/quantum-metric.directive';
import { OrbitFormClaimService } from '../../../../../services/orbit-form-claim.service';
import { SmartFixFeedbackModal } from '../../smart-fix/smart-fix-feedback/feedback-modal/smart-fix-feedback-modal.component';
import { SmartFixAnalyticsService } from '../utils/analytics/smart-fix-analytics-service';
import { BedrockChatSession, VoiceChatSession } from '../utils/interfaces/bedrock-chat.interface';
import {
  FeedbackAnswerNode,
  FeedbackQuestionNode,
  feedbackQuestionTree,
  FeedbackRadioQuestionNode,
  FeedbackResponseAnswerId,
  FeedbackResponseQuestionId,
  FeedbackTreeQuestionId,
} from './feedback-question-tree';

export interface FeedbackOption {
  label: string;
  icon?: any;
  value: boolean | null;
  children?: FeedbackOption[];
  message?: string;
  callBack?: any;
  skipTracking?: boolean;
  requiresText?: boolean;
}

@Component({
  selector: 'app-smart-fix-feedback',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    CommonModule,
    RadioButtonComponent,
    LoadingSpinnerComponent,
    TextAreaComponent,
    SmartFixFeedbackModal,
    QMEncryptDirective,
  ],
  templateUrl: './smart-fix-feedback.component.html',
  styleUrls: ['./smart-fix-feedback.component.scss', '../smart-fix.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SmartFixFeedbackComponent implements OnInit {
  client = input<BedrockChatSession | VoiceChatSession>();
  title = input<string>();
  mmrLink = input<string>('');
  solution = input<SmartFixSolution | 'unknown'>('unknown');
  feedbackText: string = '';
  showPositiveMessage: boolean = false;
  rating: string = '';
  isSubmitting: boolean = false;

  ratingScale = ['1', '2', '3', '4', '5'];
  resolvedStatus = ['Yes, it did', `No, it didn't`];

  // stepsOutcome is basically 'did the steps resolve the issue'
  readonly stepsOutcomeAnswer = computed(() => {
    const answerId = this.selectedAnswers()['stepsOutcome'];

    const question = feedbackQuestionTree['stepsOutcome'] as FeedbackRadioQuestionNode;
    const answer = question.answers.find((a) => a.id === answerId);
    return {
      problemIsResolved: answer?.problemIsResolved ?? null,
      resolvedAnswerLabel: answer?.text ?? null,
    };
  });

  starSvg: SafeHtml | null = null;
  planNumber: string = '';

  @ViewChildren('questionSection') questionSections!: QueryList<ElementRef>;

  constructor(
    private orbitClaimService: OrbitFormClaimService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private smartFixAnalytics: SmartFixAnalyticsService,
    private readonly route: ActivatedRoute
  ) {
    effect(() => {
      if (this.shownQuestions().length > 1) {
        requestAnimationFrame(() => {
          this.questionSections.last?.nativeElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
    });
  }

  protected viewModel = computed(() => {
    return {
      client: this.client(),
      title: this.title(),
      mmrLink: this.mmrLink(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  ngOnInit(): void {
    this.http.get('/assets/images/smart-fix-assets/star.svg', { responseType: 'text' }).subscribe((file) => {
      this.starSvg = this.sanitizer.bypassSecurityTrustHtml(file);
    });

    this.smartFixAnalytics.pageViewEvent('claims-sf-feedback-score');

    this.planNumber = this.route.snapshot.params['planNumber'];
  }

  setRating(star: number) {
    this.rating = star.toString();

    if (this.rating === '5') {
      this.showPositiveMessage = true;
      this.triggerConfetti();
    } else {
      this.showPositiveMessage = false;
    }
  }

  triggerConfetti() {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random(), y: 0 },
        colors: ['#0065C4', '#9848B3', '#6160C0', '#1980D8', '#FFDC5D', '#5DADEC'],
      });
    }, 250);
  }

  submitFeedback(): void {
    this.isSubmitting = true;
    this.trackFeedbackGA();

    const { surveyFeedbackResponses } = this.buildSurveyAnswersForAnalytics();

    Tracking.capture('smart_fix_submit_feedback', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      rating: this.rating,
      resolved: this.stepsOutcomeAnswer().resolvedAnswerLabel,
      feedbackText: this.feedbackText,
      solution: this.solution(),
      ...surveyFeedbackResponses,
    });

    this.vm.client
      ?.sendFeedback(this.rating, this.feedbackText, this.stepsOutcomeAnswer().problemIsResolved ?? undefined, {
        surveyFeedbackResponses,
      })

      ?.then(() => {
        if (this.stepsOutcomeAnswer().problemIsResolved) {
          window.location.href = this.vm.mmrLink;
          return;
        }
        this.orbitClaimService.moveStepForward('smart-fix');
      })
      .catch(() => {
        this.isSubmitting = false;
      });
  }

  goToBookEngineer() {
    const event = 'skip book an engineer';
    this.orbitClaimService.moveStepForward('smart-fix');
    this.smartFixAnalytics.triggerSmartFixEvent(event, '', event, 'smart-fix-feedback', 'claims-sf-feedback-score');
    Tracking.capture('smart_fix_skip_to_book_engineer', {
      productType: this.orbitClaimService?.state?.claimData?.bundle?.productType,
      isHeatingPlan: this.orbitClaimService?.state?.claimData?.bundle?.goodsColour === 'H',
      brand: this.orbitClaimService?.state?.claimData?.bundle?.manufacturer,
      solution: this.solution(),
    });
  }

  trackFeedbackGA(): void {
    const eventCategory = `smart-fix-feedback`;

    const triggerEvent = (eventLabel: string, eventAction: string) => {
      this.smartFixAnalytics.triggerSmartFixEvent(
        eventLabel,
        '',
        eventAction,
        eventCategory,
        'claims-sf-feedback-score'
      );
    };

    if (this.stepsOutcomeAnswer().resolvedAnswerLabel) {
      triggerEvent(this.stepsOutcomeAnswer().resolvedAnswerLabel!, 'did fix your issue');
    }

    if (this.rating) {
      triggerEvent(`${this.rating} star`, 'how did you rate your experience');
    }

    if (this.feedbackText) {
      triggerEvent(this.feedbackText, 'how did you like or dislike');
    }
  }

  get submitButtonLabel(): string {
    if (this.isSubmitting) {
      return '';
    }
    if (this.stepsOutcomeAnswer().problemIsResolved === true) {
      return 'Submit and cancel booking';
    }
    if (this.stepsOutcomeAnswer().problemIsResolved === false) {
      return 'Submit and continue';
    }

    return 'Submit feedback';
  }

  get isSubmitDisabled(): boolean {
    const hasRating = !!this.rating;
    const hasFeedbackText = this.feedbackText.trim().length > 0;
    const hasSurveyAnswers = Object.keys(this.selectedAnswers()).length > 0;
    return this.isSubmitting || !(hasRating || hasFeedbackText || hasSurveyAnswers);
  }

  // map of question and answer id every question the user has answered
  selectedAnswers = signal<Partial<Record<string, FeedbackResponseAnswerId>>>({});

  // list of questions to display on the UI
  shownQuestions = computed<FeedbackQuestionNode[]>(() => [
    feedbackQuestionTree['actionsTaken'],
    ...this.getAnsweredQuestions()
      .map((p) => (p.nextQuestionId ? feedbackQuestionTree[p.nextQuestionId] : undefined))
      .filter((question): question is FeedbackQuestionNode => !!question),
  ]);

  // helps determine which question comes next based on the selected answer
  private getAnsweredQuestions(): {
    question: FeedbackQuestionNode;
    answer: FeedbackAnswerNode;
    nextQuestionId: FeedbackTreeQuestionId | null;
  }[] {
    const answerPath: {
      question: FeedbackQuestionNode;
      answer: FeedbackAnswerNode;
      nextQuestionId: FeedbackTreeQuestionId | null;
    }[] = [];
    // first question
    let currentQuestion: FeedbackQuestionNode | undefined = feedbackQuestionTree['actionsTaken'];

    while (currentQuestion) {
      const selectedId: FeedbackResponseAnswerId | undefined = this.selectedAnswers()[currentQuestion.id];
      const answer: FeedbackAnswerNode | undefined = currentQuestion.answers.find(
        (candidate: FeedbackAnswerNode) => candidate.id === selectedId
      );

      if (!answer) break;

      const nextQuestionId: FeedbackTreeQuestionId | null = answer.nextQuestionId;
      answerPath.push({ question: currentQuestion, answer, nextQuestionId });
      currentQuestion = nextQuestionId ? feedbackQuestionTree[nextQuestionId] : undefined;
    }

    return answerPath;
  }

  selectAnswer(question: FeedbackQuestionNode, answer: FeedbackAnswerNode, selectedValue: string | null) {
    const path = this.shownQuestions();
    const indexQ = path.findIndex((q) => q.id === question.id);

    // update selectedAnsers with the new question
    this.selectedAnswers.update((current) => {
      const updated = { ...current };

      if (selectedValue) {
        updated[question.id] = answer.id;
      } else {
        // treat as unanswered
        delete updated[question.id];
      }

      // resets any downstream questions if an earlier question is changed
      if (indexQ >= 0) {
        path.slice(indexQ + 1).forEach(({ id }) => delete updated[id]);
      }

      return updated;
    });
  }

  private buildSurveyAnswersForAnalytics() {
    const answers: Partial<Record<FeedbackResponseQuestionId, FeedbackResponseAnswerId>> = {};

    this.getAnsweredQuestions().forEach(({ question, answer }) => {
      const questionKey = this.getTrackingQuestionId(question);
      if (!questionKey) return;
      answers[questionKey] = typeof answer.sendValue === 'string' ? answer.sendValue : answer.id;
    });

    return { surveyFeedbackResponses: answers };
  }

  private getTrackingQuestionId(question: FeedbackQuestionNode): FeedbackResponseQuestionId | null {
    const questionKey = question.sendAs || question.id;
    const trackingQuestionIds: FeedbackResponseQuestionId[] = [
      'actionsTaken',
      'stepsOutcome',
      'savedStepsReason',
      'whatsAvailable',
    ];

    return trackingQuestionIds.includes(questionKey as FeedbackResponseQuestionId)
      ? (questionKey as FeedbackResponseQuestionId)
      : null;
  }
}
